(() => {
  if (customElements.get('ormsby-artist-videos')) return;

  class OrmsbyArtistVideos extends HTMLElement {
    constructor() {
      super();
      this.index = 0;
      this.timer = null;
      this.lastFocused = null;
      this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.handleResize = () => this.goTo(this.index, { restart: false });
      this.handleKeydown = (event) => {
        if (event.key === 'Escape' && this.isModalOpen()) this.closeModal();
      };
      this.handleBlockSelect = (event) => {
        const slide = event.target?.closest?.('[data-slide]');
        if (!slide || !this.contains(slide)) return;
        const index = this.slides.indexOf(slide);
        if (index < 0) return;
        this.pause();
        this.goTo(index, { restart: false });
      };
      this.handleBlockDeselect = (event) => {
        const slide = event.target?.closest?.('[data-slide]');
        if (!slide || !this.contains(slide)) return;
        this.start();
      };
    }

    connectedCallback() {
      this.section = this.closest('.artist-videos') || this.parentElement;
      this.track = this.querySelector('[data-track]');
      this.slides = Array.from(this.querySelectorAll('[data-slide]'));
      this.prevButton = this.section?.querySelector('[data-prev]');
      this.nextButton = this.section?.querySelector('[data-next]');
      this.modal = this.section?.querySelector('[data-video-modal]');
      this.modalFrame = this.section?.querySelector('[data-modal-frame]');
      this.autoplay = this.dataset.autoplay === 'true';
      this.interval = Number(this.dataset.interval) || 8000;

      this.prevButton?.addEventListener('click', () => this.goTo(this.index - 1));
      this.nextButton?.addEventListener('click', () => this.goTo(this.index + 1));

      this.addEventListener('click', (event) => {
        const playButton = event.target.closest('[data-play]');
        if (playButton && this.contains(playButton)) {
          this.openModal(playButton);
        }
      });

      this.section?.addEventListener('click', (event) => {
        if (event.target.closest('[data-modal-close]')) this.closeModal();
      });

      this.addEventListener('mouseenter', () => this.pause());
      this.addEventListener('mouseleave', () => {
        if (!this.isModalOpen()) this.start();
      });
      this.addEventListener('focusin', () => this.pause());
      this.addEventListener('focusout', (event) => {
        if (!this.contains(event.relatedTarget) && !this.isModalOpen()) this.start();
      });

      window.addEventListener('resize', this.handleResize);
      document.addEventListener('keydown', this.handleKeydown);
      document.addEventListener('shopify:block:select', this.handleBlockSelect);
      document.addEventListener('shopify:block:deselect', this.handleBlockDeselect);

      this.goTo(0, { restart: false });
      this.start();
    }

    getVisibleCount() {
      const raw = getComputedStyle(this.closest('.artist-videos') || this).getPropertyValue('--av-visible');
      const value = Number.parseInt(raw, 10);
      return Number.isFinite(value) && value > 0 ? value : 1;
    }

    getMaxIndex() {
      return Math.max(0, this.slides.length - this.getVisibleCount());
    }

    goTo(index, { restart = true } = {}) {
      if (!this.slides.length || !this.track) return;

      const max = this.getMaxIndex();
      let next = index;
      if (next > max) next = 0;
      if (next < 0) next = max;
      this.index = next;

      const slide = this.slides[0];
      const styles = getComputedStyle(this.track);
      const gap = Number.parseFloat(styles.columnGap || styles.gap) || 0;
      const step = slide.getBoundingClientRect().width + gap;
      this.track.style.transform = `translate3d(-${this.index * step}px, 0, 0)`;

      this.slides.forEach((item, slideIndex) => {
        const inView = slideIndex >= this.index && slideIndex < this.index + this.getVisibleCount();
        item.classList.toggle('is-active', inView);
        item.setAttribute('aria-hidden', String(!inView));
      });

      if (this.prevButton) this.prevButton.disabled = max === 0;
      if (this.nextButton) this.nextButton.disabled = max === 0;

      if (restart && !this.isModalOpen()) this.start();
    }

    isModalOpen() {
      return Boolean(this.modal && !this.modal.hidden);
    }

    openModal(button) {
      const youtubeId = button.dataset.youtubeId;
      if (!youtubeId || !this.modal || !this.modalFrame) return;

      this.pause();
      this.lastFocused = button;
      this.modal.hidden = false;
      document.body.classList.add('artist-videos-modal-open');
      this.modalFrame.innerHTML = `<iframe
        src="https://www.youtube.com/embed/${youtubeId}?autoplay=1&rel=0&modestbranding=1"
        title="YouTube video"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
        allowfullscreen
      ></iframe>`;

      const closeButton = this.modal.querySelector('.artist-videos__modal-close');
      closeButton?.focus();
    }

    closeModal() {
      if (!this.modal || !this.modalFrame) return;
      this.modal.hidden = true;
      this.modalFrame.innerHTML = '';
      document.body.classList.remove('artist-videos-modal-open');
      this.lastFocused?.focus?.();
      this.lastFocused = null;
      this.start();
    }

    start() {
      this.pause();
      if (!this.autoplay || this.reduceMotion || this.getMaxIndex() < 1 || this.isModalOpen()) return;
      this.timer = window.setInterval(() => this.goTo(this.index + 1), this.interval);
    }

    pause() {
      window.clearInterval(this.timer);
      this.timer = null;
    }

    disconnectedCallback() {
      this.closeModal();
      this.pause();
      window.removeEventListener('resize', this.handleResize);
      document.removeEventListener('keydown', this.handleKeydown);
      document.removeEventListener('shopify:block:select', this.handleBlockSelect);
      document.removeEventListener('shopify:block:deselect', this.handleBlockDeselect);
    }
  }

  customElements.define('ormsby-artist-videos', OrmsbyArtistVideos);
})();
